import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import ColorPickerCustom from './ColorPickerCustom';

type InputColorProps = {
  value?: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  readOnly?: boolean;
  tooltipText?: string;
};

const InputColor = ({
  value,
  onChange,
  onFocus,
  readOnly = false,
  tooltipText,
}: InputColorProps) => {
  const [showPicker, setShowPicker] = useState(false);

  // Popper.js positioning
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'auto-start',
  });

  // Open picker
  const handleOpenPicker = () => {
    const show = !readOnly;

    setShowPicker(show);
    if (show) {
      onFocus?.();
    }
  };

  // Close picker when clicking outside
  useEffect(() => {
    if (!showPicker) return;
    function handleClick(e: MouseEvent) {
      if (
        popperElement &&
        !popperElement.contains(e.target as Node) &&
        referenceElement &&
        !referenceElement.contains(e.target as Node)
      ) {
        setShowPicker(false);
      }
    }
    document.addEventListener('pointerdown', handleClick);
    return () => document.removeEventListener('pointerdown', handleClick);
  }, [showPicker, popperElement, referenceElement]);

  // Close picker when clicking outside
  useEffect(() => {
    if (showPicker) {
      onFocus?.();
    }
  }, [showPicker, onFocus]);

  return (
    <>
      <button
        type="button"
        ref={setReferenceElement}
        className={classNames(
          'h-[37px] w-full border border-elevated-2 rounded-[5px]',
          readOnly ? 'opacity-30 cursor-default' : 'cursor-pointer'
        )}
        style={{ background: value }}
        onClick={handleOpenPicker}
        aria-label="Open color picker"
        tabIndex={readOnly ? -1 : 0}
        title={tooltipText}
      />
      {showPicker &&
        createPortal(
          <div
            ref={setPopperElement}
            style={{ ...styles.popper, zIndex: 100 }}
            className="shadow-lg rounded-lg p-2 bg-elevated-2"
            {...attributes.popper}
          >
            <ColorPickerCustom onChange={onChange} defaultValue={value} />
          </div>,
          document.body
        )}
    </>
  );
};

export default InputColor;
